import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Wizard, Steps, Step } from "react-albus";
import PanZoom, { PanZoomAPI } from "@sasza/react-panzoom";
import { cn } from "../../../services/common/className";
import ContentContainer from "../../../templates/content-container/contentContainer";
import StepsMilestones, { stepsMilestonesDataType } from "../../../ui-components/steps-milestones/stepsMilestones";
import PickupShipperStep from "./steps/pickupShipper";
import PickupConsigneeStep from "./steps/pickupConsignee";
import PickupItemsStep from "./steps/pickupItems";
import PickupSuccessStep from "./steps/pickupSuccess";
import {
  clearDeletedItems,
  createPickup,
  pickupSelector,
  resetPickupState,
  clearBolData
} from "../../../slices/pickup/pickupSlice";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import BolUpload from "./bolUpload";
import scanImg from "../../../images/scan_white.svg";
import plusImg from "../../../images/plus_gray.svg";
import minusImg from "../../../images/minus_gray.svg";
import resetImg from "../../../images/refresh.svg";
import "../../bol/bol.scss";
import "../pickup.scss";
import closeIcon from "../../../images/close_gray.svg";
import { useUploadBol } from "hooks/useUploadBol";
import { PickupRequestModel } from "app/data/pickup/models";
import { toast } from "react-toastify";
import mixpanel from "mixpanel-browser";

const initialStepsData: stepsMilestonesDataType[] = [
  {
    id: "pickup-shipper-step",
    name: "Shipper Information",
    data: null
  },
  {
    id: "pickup-consignee-step",
    name: "Consignee Information",
    data: null
  },
  {
    id: "pickup-items-step",
    name: "Detail Information",
    data: null
  },
];

const PickupCreate: React.FC<{}> = (props) => {
  const dispatch = useDispatch();
  const pickupState = useSelector(pickupSelector);
  const [stepsData, setStepsData] = useState<stepsMilestonesDataType[]>(initialStepsData);
  const [showBolUploadPopup, setShowBolUploadPopup] = useState(false);
  const panZoomRef = useRef<PanZoomAPI>();
  const [resetFormTrigger, setResetFormTrigger] = useState(0);
  const [selectedBolFile, setSelectedBolFile] = useState<File | null>(null);
  const [loadingOnSubmit, setLoadingOnSubmit] = useState(false);
  const [pickupNo, setPickupNo] = useState<string | null>(null);

  const uploadBolMutation = useUploadBol(pickupNo || "");

  const ImageControls = () => {
    return (
      <div className="xgs-bol-recognize__preview__controls">
        <div className="xgs-bol-recognize__preview__controls__zoom">
          <div className="xgs-bol-recognize__preview__controls__button no-select" onClick={() => panZoomRef.current.zoomOut(1)}><img src={minusImg} alt="-" /></div>
          <div className="xgs-bol-recognize__preview__controls__button no-select" onClick={() => panZoomRef.current.zoomIn(1)}><img src={plusImg} alt="+" /></div>
        </div>
        <div className="xgs-bol-recognize__preview__controls__button no-select" onClick={() => panZoomRef.current.reset()}><img src={resetImg} alt="Reset zoom" /></div>
      </div>
    );
  };

  useEffect(() => {
    if (!stepsData) return;
    let newStepsData = [...stepsData];
    let index = newStepsData.findIndex(step => step.id === "pickup-shipper-step");
    if (index === -1) return;
    newStepsData[index].data = pickupState.shipper && pickupState.shipper.accountNumber
      ? (<>
        <div><strong>Shipper:</strong> {pickupState.shipper.name} ({pickupState.shipper.accountNumber})</div>
        {(pickupState.shipper.contacts?.firstName || pickupState.shipper.contacts?.lastName) && (
          <div><strong>Contact person:</strong> {pickupState.shipper.contacts.firstName} {pickupState.shipper.contacts.lastName}</div>
        )}
        {pickupState.shipper.contacts?.mobilePhone && (
          <>
            <div><strong>Contact phone #:</strong> {pickupState.shipper.contacts.mobilePhone}</div>
            {pickupState.shipper.contacts?.allowSms && (
              <div>(SMS allowed)</div>
            )}
          </>
        )}
        <div><strong>Contact email:</strong> {pickupState.shipper.contacts.email}</div>
        {pickupState.bolNumber && (
          <div><strong>BOL:</strong> {pickupState.bolNumber}</div>
        )}
        {pickupState.poNumber && (
          <div><strong>PO:</strong> {pickupState.poNumber}</div>
        )}
        {pickupState.additionalReference && (
          <div><strong>Additional Ref.:</strong> {pickupState.additionalReference}</div>
        )}
        <div><strong>Pickup date:</strong> {pickupState.pickupDate}</div>
        <div><strong>Pickup time:</strong> {pickupState.pickupReadyTime} - {pickupState.pickupCloseTime}</div>
        {pickupState.specialInstructions && (
          <div><strong>Special Instructions:</strong> {pickupState.specialInstructions}</div>
        )}
      </>)
      : null;
    setStepsData([...newStepsData]);
    // eslint-disable-next-line
  }, [
    pickupState.shipper,
    pickupState.shipper.contacts,
    pickupState.bolNumber,
    pickupState.poNumber,
    pickupState.additionalReference,
    pickupState.pickupDate,
    pickupState.pickupReadyTime,
    pickupState.pickupCloseTime,
    pickupState.specialInstructions
  ]);

  useEffect(() => {
    if (!stepsData) return;
    let newStepsData = [...stepsData];
    let index = newStepsData.findIndex(step => step.id === "pickup-consignee-step");
    if (index === -1) return;
    newStepsData[index].data = (pickupState.consignee.name || (pickupState.consignee.address.address1 && pickupState.consignee.address.city) || pickupState.consignee.phone)
      ? (
        <>
          <div><strong>Consignee:</strong> {pickupState.consignee.name}</div>
          {pickupState.consignee.address.address1 && pickupState.consignee.address.city && pickupState.consignee.address.state && pickupState.consignee.address.postalCode && (
            <div><strong>Address:</strong> {pickupState.consignee.address.address1}, {pickupState.consignee.address.city}, {pickupState.consignee.address.state}, {pickupState.consignee.address.postalCode}</div>
          )}
          {pickupState.consignee.address.additionalAddressLine && (
            <div><strong>Additional address line:</strong> {pickupState.consignee.address.additionalAddressLine}</div>
          )}
          {pickupState.consignee.phone && (
            <div><strong>Phone #:</strong> {pickupState.consignee.phone}</div>
          )}
          {pickupState.consignee.email && (
            <div><strong>Email:</strong> {pickupState.consignee.email}</div>
          )}
        </>
      )
      : null;
    setStepsData([...newStepsData]);
    // eslint-disable-next-line
  }, [pickupState.consignee]);

  useEffect(() => {
    setStepsData(prevStepsData => {
      if (!prevStepsData) return prevStepsData;

      let newStepsData = [...prevStepsData];
      let index = newStepsData.findIndex(step => step.id === "pickup-items-step");
      if (index === -1) return prevStepsData;

      const itemsCount = pickupState.items.filter(item => !item.deleted).length;
      newStepsData[index].data = itemsCount > 0
        ? (<div>{itemsCount} item{itemsCount !== 1 ? "s" : ""}</div>)
        : null;

      return newStepsData;
    });
  }, [pickupState.items]);


  useEffect(() => {
    return () => {
      dispatch(resetPickupState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!pickupState.bolPreviewBlob) return;
    const blobUrl = pickupState.bolPreviewBlob;
    if (pickupState.bolPreviewType === "PDF") {
      const el = document.getElementById("pdf-viewer") as HTMLIFrameElement;
      if (el) el.src = blobUrl;
    }
  }, [pickupState.bolPreviewBlob, pickupState.bolPreviewType]);

  const handleRemoveBOL = () => {
    dispatch(clearBolData());
    dispatch(resetPickupState());
    setShowBolUploadPopup(false);
    // Trigger form reset in PickupShipperStep
    setResetFormTrigger(prev => prev + 1);
  };

  const handleSubmit = (next?: () => void) => {
    if (!pickupState.shipper) return;

    setLoadingOnSubmit(true);

    dispatch(clearDeletedItems());
    const items = pickupState.items.filter(item => !item.deleted);
    const request: PickupRequestModel = {
      teamId: pickupState.teamId,
      shipper: pickupState.shipper,
      ...(pickupState.bolNumber && { bolNumber: pickupState.bolNumber }),
      ...(pickupState.poNumber && { poNumber: pickupState.poNumber }),
      ...(pickupState.additionalReference && { additionalReference: pickupState.additionalReference }),
      pickupDate: pickupState.pickupDate.toApiDateFormat(),
      pickupReadyTime: pickupState.pickupReadyTime || "",
      pickupCloseTime: pickupState.pickupCloseTime || "",
      specialInstructions: pickupState.specialInstructions,
      consignee: pickupState.consignee,
      items: items.map(({ deleted, ...props }) => props)
    };

    dispatch(createPickup(request, (error?: any, response?: any) => {
      if (error) {
        toast.error("Failed to create pickup. Please try again.");
        setLoadingOnSubmit(false);
        return;
      } else {
        toast.info("Pickup was successfully created!");
        mixpanel.track("pickup:pickup_created");

        const pickupNoStr = parseInt(response?.pickupNumber.slice(2), 10).toString();
        setPickupNo(pickupNoStr);

        // Upload BOL only if file exists        
        if (pickupNoStr && selectedBolFile) {
          uploadBolMutation.mutate(selectedBolFile, {
            onSuccess: () => console.log("BOL uploaded successfully!"),
            onError: (err: any) => console.log(err?.message || "Failed to upload BOL"),
          });
        }

        next?.();
      }
      setLoadingOnSubmit(false);
    }));
  };

  return (
    <ContentContainer pageId={PAGE_IDS.CREATE_PICKUP} title="New Pickup">
      <div className="xgs-pickup">
        <Wizard
          render={({ step, push }) => (
            <>
              <div className="xgs-pickup__wizard">
                <Steps>
                  <Step
                    id="pickup-shipper-step"
                    render={({ next }) => (
                      <PickupShipperStep next={next} resetFormTrigger={resetFormTrigger} />
                    )}
                  />
                  <Step
                    id="pickup-consignee-step"
                    render={({ next, previous }) => (
                      <PickupConsigneeStep next={next} previous={previous} />
                    )}
                  />
                  <Step
                    id="pickup-items-step"
                    render={({ next, previous }) => (
                      <PickupItemsStep next={next} previous={previous} onSubmit={() => handleSubmit(next)} loadingOnSubmit={loadingOnSubmit} />
                    )}
                  />
                  <Step
                    id="pickup-success-step"
                    render={({ push }) => (
                      <PickupSuccessStep push={push} onStartNewPickup={() => setSelectedBolFile(null)} />
                    )}
                  />
                </Steps>
              </div>
              {!pickupState.bolPreviewBlob && (
                <div className="xgs-pickup__milestones">
                  <Button
                    theme={ButtonThemes.blue}
                    onClick={() => setShowBolUploadPopup(true)}
                    className="xgs-pickup__scan-bol__button"
                  >
                    <img src={scanImg} alt="Scan" />
                    Fill from BOL
                  </Button>
                  {showBolUploadPopup &&
                    <BolUpload
                      show
                      onClose={() => setShowBolUploadPopup(false)}
                      onSuccess={(newBolData: any, file?: File) => {
                        dispatch({
                          type: 'pickup/updateBolData', // your actual action to update BOL
                          payload: newBolData
                        });
                        if (file) {
                          setSelectedBolFile(file || null)
                        }
                        setShowBolUploadPopup(false);
                      }}
                    />
                  }
                  {stepsData && <StepsMilestones steps={stepsData} step={step} push={push} />}
                </div>
              )}
              {pickupState.bolPreviewBlob && (
                <div className="xgs-bol-recognize__container">
                  <div className="xgs-bol-recognize__preview__header">
                    <div className="xgs-bol-recognize__preview__actions">
                      <img
                        src={closeIcon}
                        className="xgs-feature-banner__close"
                        onClick={handleRemoveBOL}
                        width="20px"
                        alt="close"
                      />
                    </div>
                  </div>
                  <div
                    className={cn("xgs-bol-recognize__preview")({
                      image: pickupState.bolPreviewType !== "PDF",
                    })}
                    style={{ height: 600 }}
                  >
                    {pickupState.bolPreviewType === "PDF" ? (
                      <iframe
                        id="pdf-viewer"
                        width="100%"
                        height="100%"
                        title="Preview BOL"
                        src={pickupState.bolPreviewBlob}
                      ></iframe>
                    ) : (
                      <>
                        <div className="xgs-bol-recognize__preview__image">
                          <PanZoom ref={panZoomRef}>
                            <img
                              src={pickupState.bolPreviewBlob || undefined}
                              alt="Preview BOL"
                            />
                          </PanZoom>
                        </div>
                        <ImageControls />
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        />
      </div>
    </ContentContainer >
  );
};

export default PickupCreate;